import React, { useMemo, memo } from "react";
import dayjs from "dayjs";
import { BsFillPencilFill, BsFlagFill, BsCheckCircleFill, BsCalendarEvent, BsTrash3 } from "react-icons/bs";

const formatdate = (date) => (date ? String(date).split("T")[0] : "");

function getStartAndEndDates(obj, project) {
  let ms = Object.entries(obj || {})
    .filter(([key]) => key !== "value")
    .map(([_, dateStr]) => new Date(dateStr).getTime())
    .filter((n) => !isNaN(n));

  if (ms.length === 0) {
    ms = [
      new Date(formatdate(project?.start_project_date || "1970-01-01")).getTime(),
    ];
  }

  const min = Math.min(...ms);
  const max = Math.max(...ms);

  return {
    startDate: formatdate(new Date(min).toISOString()),
    endDate: formatdate(new Date(max).toISOString()),
  };
}

const Timeline = memo(function ProgressRow({
  item,
  days,
  timelineStart,
  onProgressClick,
  onTaskDelete,
  project,
}) {
  const processedItem = useMemo(() => {
    const sed = getStartAndEndDates(item.progress, project);
    const deadlineStr = formatdate(item.deadline);
    const startStr = sed.startDate || formatdate(project?.start_project_date) || "1970-01-01";
    const endStr = deadlineStr || sed.endDate || formatdate(project?.end_project_date) || startStr;
    const percentage = Number(item?.progress?.value || 0);

    const today = dayjs().startOf('day');
    let statusLabel = "Not Started";
    let statusColor = "#64748b";
    let statusBg = "rgba(100, 116, 139, 0.12)";
    let isOverdue = false;
    let daysDiff = null;

    if (percentage >= 100) {
      statusLabel = "Completed";
      statusColor = "#10b981";
      statusBg = "rgba(16, 185, 129, 0.12)";
    } else if (deadlineStr) {
      const deadline = dayjs(deadlineStr).startOf('day');
      daysDiff = deadline.diff(today, 'day');
      if (daysDiff < 0) {
        statusLabel = `Overdue by ${Math.abs(daysDiff)}d`;
        statusColor = "#ef4444";
        statusBg = "rgba(239, 68, 68, 0.12)";
        isOverdue = true;
      } else if (daysDiff === 0) {
        statusLabel = "Due Today";
        statusColor = "#f59e0b";
        statusBg = "rgba(245, 158, 11, 0.12)";
      } else if (daysDiff <= 3) {
        statusLabel = `${daysDiff}d left`;
        statusColor = "#f97316";
        statusBg = "rgba(249, 115, 22, 0.12)";
      } else {
        statusLabel = percentage > 0 ? "In Progress" : "Upcoming";
        statusColor = percentage > 0 ? "#3b82f6" : "#64748b";
        statusBg = percentage > 0 ? "rgba(59, 130, 246, 0.12)" : "rgba(100, 116, 139, 0.12)";
      }
    } else if (percentage > 0) {
      statusLabel = "In Progress";
      statusColor = "#3b82f6";
      statusBg = "rgba(59, 130, 246, 0.12)";
    }

    return {
      ...item,
      startDate: startStr,
      endDate: endStr,
      deadlineDate: deadlineStr,
      percentage,
      statusLabel,
      statusColor,
      statusBg,
      isOverdue,
      daysDiff
    };
  }, [item, project]);

  const totalDays = days.length;

  // Indices relative to current timeline window
  const startIndex = dayjs(processedItem.startDate).diff(dayjs(timelineStart), "day");
  const endIndex = dayjs(processedItem.endDate).diff(dayjs(timelineStart), "day");

  // Today index
  const todayIndex = dayjs().startOf('day').diff(dayjs(timelineStart), "day");

  // Clamped visible start and end
  const clampedStart = Math.max(0, startIndex);
  const clampedEnd = Math.min(totalDays - 1, endIndex);

  const isVisible = endIndex >= 0 && startIndex < totalDays;

  const leftPercent = totalDays > 0 ? (clampedStart / totalDays) * 100 : 0;
  const widthPercent = totalDays > 0 ? Math.max(2, ((clampedEnd - clampedStart + 1) / totalDays) * 100) : 0;

  // Deadline pin index
  const deadlineIndex = processedItem.deadlineDate ? dayjs(processedItem.deadlineDate).diff(dayjs(timelineStart), "day") : -1;
  const showDeadlinePin = deadlineIndex >= 0 && deadlineIndex < totalDays;

  // Gradient based on status
  let barGradient = "linear-gradient(90deg, #3b82f6, #2563eb)";
  if (processedItem.percentage >= 100) {
    barGradient = "linear-gradient(90deg, #10b981, #059669)";
  } else if (processedItem.isOverdue) {
    barGradient = "linear-gradient(90deg, #ef4444, #dc2626)";
  }

  const tooltipText = `Task: ${processedItem.task_name}
Deadline: ${processedItem.deadlineDate ? dayjs(processedItem.deadlineDate).format("DD MMM YYYY") : "No deadline"} (${processedItem.statusLabel})
Progress: ${processedItem.percentage}%
Start: ${processedItem.startDate ? dayjs(processedItem.startDate).format("DD MMM YYYY") : "N/A"}`;

  return (
    <tr className="task-row">
      <td className="task-name-col">
        <div className="task-name-wrapper">
          <span className="task-name-text" title={processedItem.task_name}>
            {processedItem.task_name}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              className="edit-task-btn"
              onClick={() => onProgressClick(processedItem)}
              title="Edit Task"
            >
              <BsFillPencilFill size={11} />
            </button>
            <button
              className="delete-task-btn"
              onClick={() => onTaskDelete && onTaskDelete(processedItem)}
              title="Delete Task"
            >
              <BsTrash3 size={11} />
            </button>
          </div>
        </div>
      </td>

      <td className="task-deadline-col">
        {processedItem.deadlineDate ? (
          <div className="deadline-pill" style={{ background: processedItem.statusBg }}>
            <span className="deadline-date-text" style={{ color: processedItem.statusColor }}>
              <BsCalendarEvent size={10} />
              {dayjs(processedItem.deadlineDate).format("DD MMM YYYY")}
            </span>
            <span
              className="deadline-status-pill"
              style={{
                color: '#ffffff',
                background: processedItem.statusColor
              }}
            >
              {processedItem.statusLabel}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>No deadline</span>
        )}
      </td>

      <td className="task-progress-col">
        <div className="progress-cell-wrapper">
          <div className="progress-track-bg">
            <div
              className="progress-fill-bar"
              style={{
                width: `${processedItem.percentage}%`,
                background: barGradient
              }}
            />
          </div>
          <span
            className="progress-percent-label"
            style={{ color: processedItem.percentage >= 100 ? '#10b981' : (processedItem.isOverdue ? '#ef4444' : '#1e293b') }}
          >
            {processedItem.percentage}%
          </span>
        </div>
      </td>

      <td colSpan={totalDays} className="timeline-gantt-cell">
        {/* Background Grid columns */}
        <div className="gantt-grid-overlay">
          {days.map((d, i) => {
            const isToday = dayjs(d).isSame(dayjs(), 'day');
            const isWeekend = dayjs(d).day() === 0 || dayjs(d).day() === 6;
            return (
              <div
                key={i}
                className={`gantt-grid-col ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''}`}
              />
            );
          })}
        </div>

        {/* Today vertical line */}
        {todayIndex >= 0 && todayIndex < totalDays && (
          <div
            className="gantt-today-line"
            style={{ left: `${((todayIndex + 0.5) / totalDays) * 100}%` }}
          />
        )}

        {/* Gantt Bar */}
        {isVisible && (
          <div
            className="gantt-bar-wrapper"
            style={{
              left: `${leftPercent}%`,
              width: `${widthPercent}%`,
            }}
            onClick={() => onProgressClick(processedItem)}
            title={tooltipText}
          >
            <div
              className="gantt-bar-fill"
              style={{
                width: `${Math.min(100, processedItem.percentage)}%`,
                background: barGradient,
              }}
            >
              {processedItem.percentage > 15 && (
                <span className="gantt-bar-text">{processedItem.percentage}%</span>
              )}
            </div>

            {/* Deadline Flag Pin on the Bar if inside */}
            {showDeadlinePin && (
              <div
                className={`gantt-deadline-pin ${processedItem.percentage >= 100 ? 'completed' : ''}`}
                title={`Deadline: ${dayjs(processedItem.deadlineDate).format("DD MMM YYYY")}`}
              >
                {processedItem.percentage >= 100 ? (
                  <BsCheckCircleFill size={10} />
                ) : (
                  <BsFlagFill size={9} />
                )}
              </div>
            )}
          </div>
        )}

        {/* If task is outside visible window: show subtle left/right indicator */}
        {!isVisible && startIndex >= totalDays && (
          <div
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 10,
              color: '#94a3b8',
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={() => onProgressClick(processedItem)}
            title={tooltipText}
          >
            Starts {dayjs(processedItem.startDate).format("DD MMM")} ➔
          </div>
        )}
        {!isVisible && endIndex < 0 && (
          <div
            style={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 10,
              color: '#94a3b8',
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={() => onProgressClick(processedItem)}
            title={tooltipText}
          >
            ⬅ Due {dayjs(processedItem.endDate).format("DD MMM")}
          </div>
        )}
      </td>
    </tr>
  );
});

export default Timeline;